/**
 * useExecutionBlocks.ts
 * Hook para carregar blocos de execução de uma etapa, com fallback v2.
 */
import { useQuery } from '@tanstack/react-query';
import type { ExecutionBlock } from '../../../types/card';
import { loadStageBlocks, loadBlockResponses, convertV2ToBlocks } from '../../shared/services/executionBlockService';
import { loadExecutionConfig } from '../../shared/services/executionConfigService';

interface UseExecutionBlocksResult {
  blocks: ExecutionBlock[];
  responses: Record<string, unknown>;
  isLoading: boolean;
  fromBlocks: boolean;
}

export function useExecutionBlocks(
  stageId: string | null | undefined,
  organizationId: string | null | undefined,
  taskId: string | null | undefined,
  metadataFallback?: any,
): UseExecutionBlocksResult {
  const blocksQuery = useQuery({
    queryKey: ['execution-blocks', stageId, organizationId],
    enabled: Boolean(stageId && organizationId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const blocks = await loadStageBlocks(stageId!, organizationId!);

      // If blocks exist in v1 table, use them
      if (blocks.length > 0) {
        return { blocks, fromBlocks: true };
      }

      // Fallback 1: check task metadata for blocks (saved from map editor)
      // metadataFallback can be either { metadata: { executionConfig } } (task object) or { executionConfig } (raw metadata)
      const execCfg = metadataFallback?.metadata?.executionConfig ?? metadataFallback?.executionConfig;
      const metaBlocks = execCfg?.blocks as ExecutionBlock[] | undefined;
      if (metaBlocks && metaBlocks.length > 0) {
        return { blocks: metaBlocks, fromBlocks: true };
      }

      // Fallback 2: load v2 config and convert to blocks
      const metaForV2 = metadataFallback?.metadata ?? metadataFallback;
      const v2Config = await loadExecutionConfig(stageId!, organizationId!, metaForV2);
      if (v2Config && (v2Config.fields.length > 0 || v2Config.checklist.length > 0)) {
        const converted = convertV2ToBlocks(stageId!, organizationId!, {
          fields: v2Config.fields,
          checklist: v2Config.checklist,
          rules: v2Config.rules,
          requireAttachment: v2Config.requireAttachment,
          requireJustification: v2Config.requireJustification,
          requirePhoto: v2Config.requirePhoto,
          instruction: v2Config.instruction ?? undefined,
        });
        return { blocks: converted, fromBlocks: false };
      }

      return { blocks: [], fromBlocks: false };
    },
  });

  const responsesQuery = useQuery({
    queryKey: ['execution-block-responses', taskId, organizationId],
    enabled: Boolean(taskId && organizationId),
    staleTime: 30_000,
    queryFn: () => loadBlockResponses(taskId!, organizationId!),
  });

  return {
    blocks: blocksQuery.data?.blocks ?? [],
    responses: responsesQuery.data ?? {},
    isLoading: blocksQuery.isLoading || responsesQuery.isLoading,
    fromBlocks: blocksQuery.data?.fromBlocks ?? false,
  };
}
